import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Recommendation } from '@lizrd/contracts';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';
import { Spinner } from '../ui/spinner';
import { usd } from '../../lib/graph-visuals';

/**
 * The NIS2 compliance guardrail banner shown on a cost recommendation when acting
 * on it would weaken a control. Rendered on both the optimization modal (compact)
 * and the detail page (full). When the customer accepts the compliant alternative,
 * `onAcceptAlternative` regenerates the recommendation's guidance toward that
 * change. Once accepted (`applied`), the banner collapses to a confirmation —
 * expandable — and goes green.
 */
export function GuardrailBanner({
  rec,
  domainId,
  compact = false,
  canManage = false,
  onAcceptAlternative,
  accepting = false,
  applied = false,
}: {
  rec: Recommendation;
  domainId: string;
  compact?: boolean;
  canManage?: boolean;
  onAcceptAlternative?: (intent: string) => void;
  accepting?: boolean;
  applied?: boolean;
}) {
  const flags = rec.complianceFlags;
  // Collapsed by default once the alternative is applied; open while unresolved.
  const [open, setOpen] = useState(!applied);
  useEffect(() => setOpen(!applied), [applied]);

  if (!flags || flags.length === 0) return null;
  const firstAlt = flags.find((f) => f.compliantAlternative)?.compliantAlternative ?? null;
  const variant = applied
    ? 'success'
    : flags.some((f) => f.gate === 'block')
      ? 'danger'
      : 'warning';

  return (
    <Alert variant={variant} className={compact ? 'p-3' : undefined}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`font-semibold text-bone ${compact ? 'text-sm' : ''}`}>
            {applied
              ? 'NIS2-compliant alternative applied'
              : 'Compliance guardrail — this would weaken a NIS2 control'}
          </div>
          {applied && !open && (
            <div className="mt-0.5 text-xs text-muted">
              The guidance below follows the compliant alternative.
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex-none text-xs font-semibold text-iris hover:text-iris-strong"
        >
          {open ? 'Hide' : 'Details'}
        </button>
      </div>

      {open && (
        <>
          <div className={compact ? 'mt-1.5 space-y-2' : 'mt-2 space-y-3'}>
            {flags.map((f, i) => (
              <div key={i}>
                <div className="text-sm text-haze">{f.message}</div>
                {f.compliantAlternative && (
                  <div className="mt-1 text-sm">
                    <span className="font-semibold text-bone">Compliant alternative: </span>
                    <span className="text-haze">{f.compliantAlternative}</span>
                    {f.compliantAlternativeSaving != null && (
                      <span className="text-muted">
                        {' '}
                        (still saves ~{usd(f.compliantAlternativeSaving)}/mo
                        {f.compliantAlternativeBasis ? ` — ${f.compliantAlternativeBasis}` : ''})
                      </span>
                    )}
                  </div>
                )}
                <div className="mt-1 font-mono text-[0.62rem] text-muted">
                  {f.controlLabel} · {f.nis2Measure}
                </div>
              </div>
            ))}
          </div>

          {applied ? (
            <div className="mt-2 flex items-center gap-1 text-xs font-semibold text-success">
              ✓ Alternative applied — the guidance below now follows it
            </div>
          ) : firstAlt && canManage && onAcceptAlternative ? (
            <div className="mt-2">
              <Button
                size="sm"
                variant="outline"
                disabled={accepting}
                onClick={() => onAcceptAlternative(firstAlt)}
              >
                {accepting ? <Spinner size="sm" /> : 'Accept alternative'}
              </Button>
            </div>
          ) : null}

          <Link
            to={`/domains/${domainId}/compliance`}
            className="mt-3 block text-xs font-semibold text-iris hover:text-iris-strong"
          >
            View in NIS2 Compliance →
          </Link>
        </>
      )}
    </Alert>
  );
}
